/**
 * ProducerDetailPage — /feed/producers/:name (US-048-09)
 *
 * One page per feed producer: owned settings (d6, d22), run/skip history,
 * the last 10 published entries, and the settings audit trail. Page-local
 * state, fetch on mount, re-fetch after every successful save (design spec
 * section 10) — no optimistic flips, because a false "saved" is worse than a
 * slow one (same rule as the kill switch on ProducersListPage).
 *
 * Journals.API cold start can take ~90 s; every read goes through the
 * service's COLDSTART_RETRY_CONFIG. Never shorten those timeouts.
 */

import { startTransition, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Lock, RefreshCw } from 'lucide-react';
import {
  getProducerAudit,
  getProducerDetail,
  getProducerEntries,
  getProducerRuns,
  updateProducerSettings,
  ProducersHttpError,
  type ProducerDetail,
  type ProducerEntry,
  type ProducerFieldValue,
  type ProducerRunSummary,
  type ProducerSettingAudit,
  type ProducerSettingsPatch,
} from '../../services/adminProducersService';
import { fmtUtcDateTime } from '../../utils/formatUtc';

type OwnedFieldKey = 'enabled' | 'thresholds' | 'idleDays' | 'runDay' | 'runHourUtc';

const FIELD_LABELS: Record<OwnedFieldKey, string> = {
  enabled: 'Enabled',
  thresholds: 'Thresholds',
  idleDays: 'IdleDays',
  runDay: 'RunDay',
  runHourUtc: 'RunHourUtc',
};

const FIELD_HELP: Record<OwnedFieldKey, string> = {
  enabled: 'Turn this producer off to stop it writing anything new.',
  thresholds: 'Ascending percentages 1-100, comma separated.',
  idleDays: '3-90 days.',
  runDay: 'The day of the week this producer runs, UTC.',
  runHourUtc: '0-23, UTC.',
};

const RUN_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

const SKIP_COLUMNS: ReadonlyArray<[keyof ProducerRunSummary, string]> = [
  ['skippedPrivacyFloor', 'Privacy floor'],
  ['skippedConsentOff', 'Consent off'],
  ['skippedDailyCap', 'Daily cap'],
  ['skippedDuplicate', 'Duplicate'],
  ['skippedNotSubstantive', 'Not substantive'],
  ['skippedDisabled', 'Disabled'],
];

const VISIBILITY_LABEL: Record<ProducerEntry['visibility'], string> = {
  peers: 'Peers',
  private: 'Private',
  aggregate: 'Aggregate',
};

const NO_RUNS_COPY = 'No runs recorded yet. This producer uses the default settings from appsettings.';
const NO_ENTRIES_COPY = 'No entries published yet.';
const NO_AUDIT_COPY = 'No settings changes yet.';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function renderAuditValue(value: string | null | undefined): string {
  return value ?? 'default';
}

function DefaultBadge({ testId }: { testId: string }) {
  return (
    <span
      data-testid={testId}
      className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-zinc-800 text-zinc-400 border border-zinc-700"
    >
      default
    </span>
  );
}

// ---------------------------------------------------------------------------
// ProducerSettingsForm
// ---------------------------------------------------------------------------

interface FieldShellProps {
  fieldKey: OwnedFieldKey;
  defaultDisplay: string;
  isDefault: boolean;
  saving: boolean;
  error: string | null;
  onReset: () => void;
  children: React.ReactNode;
}

function FieldShell({ fieldKey, defaultDisplay, isDefault, saving, error, onReset, children }: FieldShellProps) {
  const label = FIELD_LABELS[fieldKey];
  return (
    <div
      data-testid={`producer-field-${fieldKey}`}
      className="flex flex-col gap-2 p-4 bg-zinc-900 border border-zinc-800 rounded-xl"
    >
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={`field-${fieldKey}`} className="text-sm font-medium text-zinc-100">
          {label}
        </label>
        {isDefault && <DefaultBadge testId={`producer-field-${fieldKey}-default-badge`} />}
      </div>
      <p className="text-xs text-zinc-500">{FIELD_HELP[fieldKey]}</p>
      {children}
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-zinc-500">Default: {defaultDisplay}</span>
        <button
          type="button"
          onClick={onReset}
          disabled={saving}
          aria-label={`Reset ${label} to default`}
          className="min-h-[48px] px-3 rounded-lg border border-zinc-600 text-zinc-300 text-xs hover:bg-zinc-800 disabled:opacity-50"
        >
          Reset to default
        </button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

interface FieldState {
  saving: boolean;
  error: string | null;
}

function EnabledField({
  field,
  producer,
  state,
  onSave,
  onReset,
}: {
  field: ProducerFieldValue<boolean>;
  producer: string;
  state: FieldState;
  onSave: (value: boolean) => Promise<void>;
  onReset: () => void;
}) {
  function handleToggle() {
    if (field.value) {
      const confirmed = window.confirm(
        `Stop ${producer}? The next run or message writes nothing. Entries already published stay.`,
      );
      if (!confirmed) return;
      void onSave(false);
    } else {
      void onSave(true);
    }
  }

  return (
    <FieldShell
      fieldKey="enabled"
      defaultDisplay={field.default ? 'On' : 'Off'}
      isDefault={field.isDefault}
      saving={state.saving}
      error={state.error}
      onReset={onReset}
    >
      <button
        id="field-enabled"
        type="button"
        role="switch"
        aria-checked={field.value}
        aria-busy={state.saving}
        disabled={state.saving}
        onClick={handleToggle}
        className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-70 ${
          field.value ? 'bg-violet-600' : 'bg-zinc-700'
        }`}
      >
        <span
          className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-white transition-transform ${
            field.value ? 'translate-x-[22px]' : 'translate-x-0.5'
          }`}
        />
      </button>
    </FieldShell>
  );
}

function ThresholdsField({
  field,
  state,
  onSave,
  onReset,
}: {
  field: ProducerFieldValue<number[]>;
  state: FieldState;
  onSave: (value: number[]) => Promise<void>;
  onReset: () => void;
}) {
  // Adjusts the draft when the server value changes (after a save/reset
  // re-fetch) without an effect — React's documented pattern for deriving
  // state from a changed prop during render instead of a post-render effect.
  const [renderedValue, setRenderedValue] = useState(field.value);
  const [draft, setDraft] = useState(field.value.join(', '));
  const [parseError, setParseError] = useState<string | null>(null);
  if (renderedValue !== field.value) {
    setRenderedValue(field.value);
    setDraft(field.value.join(', '));
  }

  function parse(raw: string): number[] | null {
    const parts = raw.split(',').map((p) => p.trim()).filter((p) => p.length > 0);
    if (parts.length === 0) return null;
    const nums = parts.map((p) => Number(p));
    if (nums.some((n) => !Number.isInteger(n))) return null;
    return nums;
  }

  async function handleSave() {
    const parsed = parse(draft);
    if (parsed === null) {
      setParseError('Enter whole numbers separated by commas, e.g. 25, 50, 75.');
      return;
    }
    setParseError(null);
    await onSave(parsed);
  }

  return (
    <FieldShell
      fieldKey="thresholds"
      defaultDisplay={field.default.join(', ')}
      isDefault={field.isDefault}
      saving={state.saving}
      error={state.error ?? parseError}
      onReset={onReset}
    >
      <div className="flex items-center gap-2">
        <input
          id="field-thresholds"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={state.saving}
          aria-label="Thresholds"
          aria-invalid={parseError !== null || state.error !== null}
          className="flex-1 rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-violet-500"
        />
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={state.saving}
          className="min-h-[48px] px-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-zinc-950 text-sm"
        >
          {state.saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </FieldShell>
  );
}

function NumberField({
  fieldKey,
  field,
  min,
  max,
  state,
  onSave,
  onReset,
}: {
  fieldKey: 'idleDays' | 'runHourUtc';
  field: ProducerFieldValue<number>;
  min: number;
  max: number;
  state: FieldState;
  onSave: (value: number) => Promise<void>;
  onReset: () => void;
}) {
  const [renderedValue, setRenderedValue] = useState(field.value);
  const [draft, setDraft] = useState(String(field.value));
  const [parseError, setParseError] = useState<string | null>(null);
  if (renderedValue !== field.value) {
    setRenderedValue(field.value);
    setDraft(String(field.value));
  }

  async function handleSave() {
    const value = Number(draft);
    if (draft.trim() === '' || !Number.isInteger(value) || value < min || value > max) {
      setParseError(`Enter a whole number between ${min} and ${max}.`);
      return;
    }
    setParseError(null);
    await onSave(value);
  }

  return (
    <FieldShell
      fieldKey={fieldKey}
      defaultDisplay={String(field.default)}
      isDefault={field.isDefault}
      saving={state.saving}
      error={state.error ?? parseError}
      onReset={onReset}
    >
      <div className="flex items-center gap-2">
        <input
          id={`field-${fieldKey}`}
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={state.saving}
          aria-label={FIELD_LABELS[fieldKey]}
          aria-invalid={parseError !== null || state.error !== null}
          className="w-28 rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-violet-500"
        />
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={state.saving}
          className="min-h-[48px] px-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-zinc-950 text-sm"
        >
          {state.saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </FieldShell>
  );
}

function RunDayField({
  field,
  state,
  onSave,
  onReset,
}: {
  field: ProducerFieldValue<string>;
  state: FieldState;
  onSave: (value: string) => Promise<void>;
  onReset: () => void;
}) {
  const [renderedValue, setRenderedValue] = useState(field.value);
  const [draft, setDraft] = useState(field.value);
  if (renderedValue !== field.value) {
    setRenderedValue(field.value);
    setDraft(field.value);
  }

  return (
    <FieldShell
      fieldKey="runDay"
      defaultDisplay={field.default}
      isDefault={field.isDefault}
      saving={state.saving}
      error={state.error}
      onReset={onReset}
    >
      <div className="flex items-center gap-2">
        <select
          id="field-runDay"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={state.saving}
          aria-label="RunDay"
          className="rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-violet-500"
        >
          {RUN_DAYS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void onSave(draft)}
          disabled={state.saving}
          className="min-h-[48px] px-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-zinc-950 text-sm"
        >
          {state.saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </FieldShell>
  );
}

function MinGroupSize({ value }: { value: number }) {
  return (
    <div
      data-testid="producer-min-group-size"
      className="flex items-start gap-3 p-4 bg-zinc-900 border border-zinc-800 rounded-xl"
    >
      <Lock className="w-4 h-4 mt-0.5 text-zinc-500" aria-hidden="true" />
      <div>
        <p className="text-sm font-medium text-zinc-100">MinGroupSize</p>
        <p className="text-sm text-zinc-300 mt-1">{value}</p>
        <p className="text-xs text-zinc-500 mt-1">Privacy floor - fixed at 5 in code</p>
      </div>
    </div>
  );
}

function ProducerSettingsForm({
  producer,
  detail,
  fieldStates,
  onSaveField,
  onResetField,
}: {
  producer: string;
  detail: ProducerDetail;
  fieldStates: Record<OwnedFieldKey, FieldState>;
  onSaveField: (key: OwnedFieldKey, value: unknown) => Promise<void>;
  onResetField: (key: OwnedFieldKey) => Promise<void>;
}) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-zinc-100">Settings</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {detail.enabled && (
          <EnabledField
            field={detail.enabled}
            producer={producer}
            state={fieldStates.enabled}
            onSave={(v) => onSaveField('enabled', v)}
            onReset={() => void onResetField('enabled')}
          />
        )}
        {detail.thresholds && (
          <ThresholdsField
            field={detail.thresholds}
            state={fieldStates.thresholds}
            onSave={(v) => onSaveField('thresholds', v)}
            onReset={() => void onResetField('thresholds')}
          />
        )}
        {detail.idleDays && (
          <NumberField
            fieldKey="idleDays"
            field={detail.idleDays}
            min={3}
            max={90}
            state={fieldStates.idleDays}
            onSave={(v) => onSaveField('idleDays', v)}
            onReset={() => void onResetField('idleDays')}
          />
        )}
        {detail.runDay && (
          <RunDayField
            field={detail.runDay}
            state={fieldStates.runDay}
            onSave={(v) => onSaveField('runDay', v)}
            onReset={() => void onResetField('runDay')}
          />
        )}
        {detail.runHourUtc && (
          <NumberField
            fieldKey="runHourUtc"
            field={detail.runHourUtc}
            min={0}
            max={23}
            state={fieldStates.runHourUtc}
            onSave={(v) => onSaveField('runHourUtc', v)}
            onReset={() => void onResetField('runHourUtc')}
          />
        )}
        <MinGroupSize value={detail.minGroupSize} />
      </div>
      {detail.nextScheduledRunUtc && (
        <p className="text-xs text-zinc-400">
          Next scheduled run: {fmtUtcDateTime(detail.nextScheduledRunUtc)}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SkipReasonHistoryTable
// ---------------------------------------------------------------------------

function SkipReasonHistoryTable({
  runs,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  runs: ProducerRunSummary[];
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-zinc-100">Run history</h2>
      {runs.length === 0 ? (
        <p className="text-sm text-zinc-500">{NO_RUNS_COPY}</p>
      ) : (
        <div className="overflow-x-auto bg-zinc-900 border border-zinc-800 rounded-xl">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-[11px] uppercase tracking-wide text-zinc-500">
                <th scope="col" className="px-4 py-2 font-semibold">Run</th>
                <th scope="col" className="px-4 py-2 font-semibold">Started</th>
                <th scope="col" className="px-4 py-2 font-semibold text-right">Published</th>
                {SKIP_COLUMNS.map(([, label]) => (
                  <th key={label} scope="col" className="px-4 py-2 font-semibold text-right">
                    {label}
                  </th>
                ))}
                <th scope="col" className="px-4 py-2 font-semibold text-right">Failed</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.runKey}
                  data-testid={`run-row-${run.runKey}`}
                  className="border-b border-zinc-800"
                >
                  <td className="px-4 py-2 text-zinc-200">{run.runKey}</td>
                  <td className="px-4 py-2 text-zinc-400 whitespace-nowrap">{fmtUtcDateTime(run.startedAt)}</td>
                  <td className="px-4 py-2 text-right text-zinc-200">{run.published}</td>
                  {SKIP_COLUMNS.map(([key]) => (
                    <td key={key} className="px-4 py-2 text-right text-zinc-300">
                      {run[key] as number}
                    </td>
                  ))}
                  <td className={`px-4 py-2 text-right font-medium ${run.failed > 0 ? 'text-red-400' : 'text-zinc-300'}`}>
                    {run.failed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loadingMore}
          className="self-start min-h-[48px] px-3 rounded-lg border border-zinc-600 text-zinc-300 text-sm hover:bg-zinc-800 disabled:opacity-50"
        >
          {loadingMore ? 'Loading…' : 'Older runs'}
        </button>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// RecentEntriesPreview
// ---------------------------------------------------------------------------

function entryState(entry: ProducerEntry): string {
  if (entry.isDeleted) return 'Removed';
  if (entry.isHidden) return 'Hidden';
  return 'Visible';
}

function RecentEntriesPreview({ producer, entries }: { producer: string; entries: ProducerEntry[] }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-zinc-100">Recent entries</h2>
      {entries.length === 0 ? (
        <p className="text-sm text-zinc-500">{NO_ENTRIES_COPY}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {entries.map((entry) => (
            <li
              key={entry.journalId}
              data-testid={`entry-row-${entry.journalId}`}
              className="flex flex-wrap items-start justify-between gap-3 p-3 bg-zinc-900 border border-zinc-800 rounded-xl"
            >
              <div className="min-w-0 max-w-md">
                <p className="text-sm font-medium text-zinc-100">{entry.title}</p>
                <p className="text-xs text-zinc-500 truncate">{entry.summary}</p>
                <p className="text-xs text-zinc-600 mt-1">{fmtUtcDateTime(entry.createdAt)}</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-400">
                <span>{VISIBILITY_LABEL[entry.visibility]}</span>
                <span>{entry.recipientCount} recipients</span>
                <span>{entryState(entry)}</span>
                <Link
                  to={`/moderation/posts?producer=${encodeURIComponent(producer)}&journalId=${entry.journalId}`}
                  className="text-violet-400 hover:text-violet-300 underline"
                >
                  Moderate
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SettingsAuditList
// ---------------------------------------------------------------------------

function SettingsAuditList({ audit }: { audit: ProducerSettingAudit[] }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-zinc-100">Settings audit</h2>
      {audit.length === 0 ? (
        <p className="text-sm text-zinc-500">{NO_AUDIT_COPY}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {audit.map((row, i) => (
            <li
              key={`${row.field}-${row.changedAt}-${i}`}
              data-testid="audit-row"
              className="flex flex-wrap items-center gap-2 p-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm"
            >
              <span className="text-zinc-400">{fmtUtcDateTime(row.changedAt)}</span>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-300">{row.changedBy}</span>
              <span className="text-zinc-500">·</span>
              <span className="font-medium text-zinc-100">{row.field}</span>
              <span className="text-zinc-400">
                {renderAuditValue(row.oldValue)} -&gt; {renderAuditValue(row.newValue)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const EMPTY_FIELD_STATES: Record<OwnedFieldKey, FieldState> = {
  enabled: { saving: false, error: null },
  thresholds: { saving: false, error: null },
  idleDays: { saving: false, error: null },
  runDay: { saving: false, error: null },
  runHourUtc: { saving: false, error: null },
};

export function ProducerDetailPage() {
  const { name } = useParams<{ name: string }>();
  const producer = name ?? '';

  const [detail, setDetail] = useState<ProducerDetail | null>(null);
  const [runs, setRuns] = useState<ProducerRunSummary[]>([]);
  const [runsPage, setRunsPage] = useState(0);
  const [runsHasMore, setRunsHasMore] = useState(false);
  const [loadingMoreRuns, setLoadingMoreRuns] = useState(false);
  const [entries, setEntries] = useState<ProducerEntry[]>([]);
  const [audit, setAudit] = useState<ProducerSettingAudit[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [fieldStates, setFieldStates] = useState<Record<OwnedFieldKey, FieldState>>(EMPTY_FIELD_STATES);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setNotFound(false);
    try {
      const [detailRes, runsRes, entriesRes, auditRes] = await Promise.all([
        getProducerDetail(producer),
        getProducerRuns(producer, 0),
        getProducerEntries(producer),
        getProducerAudit(producer, 0),
      ]);
      setDetail(detailRes);
      setRuns(runsRes.items);
      setRunsPage(0);
      setRunsHasMore(runsRes.hasMore);
      setEntries(entriesRes);
      setAudit(auditRes.items);
    } catch (err) {
      if (err instanceof ProducersHttpError && err.status === 404) {
        setNotFound(true);
      } else {
        setLoadError(errorMessage(err, 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  }, [producer]);

  useEffect(() => {
    startTransition(() => { void load(); });
  }, [load]);

  async function loadMoreRuns() {
    setLoadingMoreRuns(true);
    try {
      const next = runsPage + 1;
      const res = await getProducerRuns(producer, next);
      setRuns((prev) => [...prev, ...res.items]);
      setRunsPage(next);
      setRunsHasMore(res.hasMore);
    } finally {
      setLoadingMoreRuns(false);
    }
  }

  // Re-fetches detail + audit after a successful save, so the form renders
  // the server's value and the new audit row shows up (AC4, AC7). Never
  // flips a field's displayed value from the local draft alone.
  const refreshAfterSave = useCallback(async () => {
    const [detailRes, auditRes] = await Promise.all([
      getProducerDetail(producer),
      getProducerAudit(producer, 0),
    ]);
    setDetail(detailRes);
    setAudit(auditRes.items);
  }, [producer]);

  async function saveField(key: OwnedFieldKey, value: unknown) {
    setFieldStates((s) => ({ ...s, [key]: { saving: true, error: null } }));
    try {
      const patch: ProducerSettingsPatch = { [key]: value };
      await updateProducerSettings(producer, patch);
      await refreshAfterSave();
      setFieldStates((s) => ({ ...s, [key]: { saving: false, error: null } }));
    } catch (err) {
      const serverMessage =
        err instanceof ProducersHttpError ? err.fieldErrors[key]?.[0] : undefined;
      setFieldStates((s) => ({
        ...s,
        [key]: { saving: false, error: serverMessage ?? errorMessage(err, 'Could not save this field.') },
      }));
    }
  }

  async function resetField(key: OwnedFieldKey) {
    await saveField(key, null);
  }

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6 gap-6">
      <div>
        <Link
          to="/feed/producers"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
          Back to Feed producers
        </Link>
        <h1 className="text-sm font-semibold text-zinc-100">{producer}</h1>
      </div>

      {loading && !detail && !notFound && !loadError && (
        <div role="status" aria-label="Loading producer" className="flex flex-col gap-2">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-16 rounded-lg bg-zinc-900 border border-zinc-800 motion-safe:animate-pulse" />
          ))}
        </div>
      )}

      {notFound && (
        <div className="flex flex-col gap-2 items-start p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
          <p className="text-sm text-zinc-200">Unknown producer '{producer}'.</p>
          <Link to="/feed/producers" className="text-sm text-violet-400 hover:text-violet-300 underline">
            Back to Feed producers
          </Link>
        </div>
      )}

      {loadError && !detail && (
        <div role="alert" className="flex flex-col gap-2 items-start p-4 bg-zinc-900 border border-red-900/40 rounded-xl">
          <div className="flex items-center gap-1.5 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            <span>Could not load producer</span>
          </div>
          <p className="text-xs text-zinc-500 break-words">{loadError}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 min-h-[48px] px-3 rounded-lg bg-zinc-800 text-zinc-300 text-sm hover:bg-zinc-700"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Retry
          </button>
        </div>
      )}

      {detail && (
        <>
          <ProducerSettingsForm
            producer={producer}
            detail={detail}
            fieldStates={fieldStates}
            onSaveField={saveField}
            onResetField={resetField}
          />
          <SkipReasonHistoryTable
            runs={runs}
            hasMore={runsHasMore}
            loadingMore={loadingMoreRuns}
            onLoadMore={() => void loadMoreRuns()}
          />
          <RecentEntriesPreview producer={producer} entries={entries} />
          <SettingsAuditList audit={audit} />
        </>
      )}
    </main>
  );
}

export default ProducerDetailPage;
